import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { SearchBar } from '../../atoms';
import {
    UtilisateurChoice,
    UtilisateurChoiceItem,
} from '../UtilisateurChoiceItem/UtilisateurChoiceItem';

export type UtilisateurSearchListProps = {
    users: UtilisateurChoice[];
    selectedUser?: UtilisateurChoice;
    onSelectUser: (choice?: UtilisateurChoice) => void;
    extraSearchBarButton?: JSX.Element;
    listTitle?: string;
    userInfo?: (user: UtilisateurChoice) => string;
    onSearch?: (searchTerm: string) => void;
    isSearching?: boolean;
};
export const UtilisateurSearchList: React.FunctionComponent<UtilisateurSearchListProps> =
    function ({
        users,
        selectedUser,
        onSelectUser,
        extraSearchBarButton,
        listTitle,
        userInfo,
        onSearch,
        isSearching,
    }) {
        const { palette } = useTheme();
        const [searchBarValue, setSearchBarValue] = useState('');

        const handleSearch = useDebouncedCallback((value: string) => {
            if (onSearch) {
                onSearch(value);
            }
        }, 300);

        const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setSearchBarValue(value);
            handleSearch(value.trim());
        };

        // Préparer la liste affichée : mettre le selectedUser en premier
        const selectedId = selectedUser?.id;
        const filteredWithoutSelected = selectedId
            ? users.filter((u) => u.id !== selectedId)
            : users;
        const listToDisplay = selectedUser ? [selectedUser, ...filteredWithoutSelected] : users;

        return (
            <>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                    {extraSearchBarButton}
                    <SearchBar
                        placeholder="Rechercher un utilisateur par nom, prénom, mail..."
                        onChange={onSearchTermChange}
                        value={searchBarValue}
                        sx={{ marginTop: 0 }}
                    />
                </Box>
                {listTitle && (
                    <Typography
                        variant="l2dbodyxs"
                        sx={{ color: 'text.gris70' }}
                    >
                        {listTitle}
                    </Typography>
                )}
                <Box
                    data-testid="applying-members-list-container"
                    sx={{ maxHeight: '200px', overflow: 'auto' }}
                >
                    {isSearching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography
                                variant="l2dbodyxs"
                                sx={{ color: 'text.gris70' }}
                            >
                                Recherche en cours...
                            </Typography>
                        </Box>
                    ) : (
                        listToDisplay.map((user) => (
                            <UtilisateurChoiceItem
                                key={user.id}
                                user={user}
                                onSelectUser={onSelectUser}
                                selected={Boolean(selectedUser && selectedUser.id === user.id)}
                                userInfo={userInfo ? userInfo(user) : undefined}
                                sx={{
                                    ':hover': {
                                        backgroundColor: palette.grey.A700,
                                    },
                                }}
                            />
                        ))
                    )}
                </Box>
            </>
        );
    };
